import { Fragment, useEffect, useState } from "react";

import {
  IUpdateWardConfigurationRequest,
  IWard,
  TOnboardingStrategy,
} from "../../../../@types/admin/AdminWard";
import { SelectDropdown } from "../../../../components/SelectDropdown/SelectDropdown";
import { useUpdateWardConfiguration } from "../../../../hooks/mutations/admin/wards";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  FormSubsection,
  FormSubsectionTitle,
  SectionActions,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionTitleGroup,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";
import { PageInfoTooltip } from "../PageInfoTooltip";
import {
  EMED_MRN_KEY_OPTIONS,
  EXCLUSIVE_OXIMETER_DEVICES,
  MANDATORY_SMART_DEVICES,
  ONBOARDING_STRATEGY_OPTIONS,
  ONE_MSTS_THRESHOLD_MAX,
  ONE_MSTS_THRESHOLD_MIN,
  SMART_DEVICE_OPTIONS,
  SPIROMETRY_RESULT_TYPE_OPTIONS,
  TWardFeatureFlagKey,
  WARD_FEATURE_SWITCH_GROUPS,
  applyWardFlagChange,
} from "./wardConfigFields";
import { FlagRow } from "./WardConfigurationForm.styles";

interface IWardConfigurationFormProps {
  ward: IWard;
}

interface IWardConfigurationFormState {
  onboardingStrategy: TOnboardingStrategy;
  flags: Record<TWardFeatureFlagKey, boolean>;
  eMedMrnKey: string;
  /** Kept as text; empty means "no override" (the app default applies). */
  oneMstsPulseOxThreshold: string;
  spirometryResultTypes: string[];
  /** Empty means "no override" — devices derive from the condition type. */
  enabledSmartDevices: string[];
}

const ALL_FLAG_KEYS = WARD_FEATURE_SWITCH_GROUPS.flatMap((group) =>
  group.switches.map((flag) => flag.key)
);

function toFormState(ward: IWard): IWardConfigurationFormState {
  return {
    onboardingStrategy: ward.onboardingStrategy ?? "LegacyApp",
    flags: Object.fromEntries(
      ALL_FLAG_KEYS.map((key) => [key, ward[key] ?? false])
    ) as Record<TWardFeatureFlagKey, boolean>,
    eMedMrnKey: ward.eMedMrnKey ?? "",
    oneMstsPulseOxThreshold:
      ward.oneMstsPulseOxThreshold != null
        ? String(ward.oneMstsPulseOxThreshold)
        : "",
    spirometryResultTypes: ward.spirometryResultTypes ?? [],
    enabledSmartDevices: ward.enabledSmartDevices ?? [],
  };
}

function thresholdError(raw: string): string | undefined {
  if (!raw.trim()) return undefined;
  const value = Number(raw);
  if (
    !Number.isInteger(value) ||
    value < ONE_MSTS_THRESHOLD_MIN ||
    value > ONE_MSTS_THRESHOLD_MAX
  ) {
    return `Enter a whole percentage between ${ONE_MSTS_THRESHOLD_MIN} and ${ONE_MSTS_THRESHOLD_MAX}, or leave empty for the app default.`;
  }
  return undefined;
}

function smartDevicesError(selected: string[]): string | undefined {
  if (selected.length === 0) return undefined;
  if (!MANDATORY_SMART_DEVICES.every((device) => selected.includes(device))) {
    return "The MIR Spirometer is required whenever a device list is set — there is no manual fallback for spirometry.";
  }
  const oximeters = selected.filter((device) =>
    EXCLUSIVE_OXIMETER_DEVICES.includes(device)
  );
  if (oximeters.length > 1) {
    return "Only one oximeter can be enabled at a time.";
  }
  return undefined;
}

/**
 * Ward feature configuration — the write path Retool's `formWard` bundled into
 * the ward PUT, now on its own PUT /Retool/Wards/{id}/Configuration (EN-5195).
 * The endpoint replaces the whole configuration, so this form always submits
 * every field from its loaded state.
 *
 * Saving pushes a silent `configChanged` notification to the ward's patients,
 * so the app picks changes up immediately; clinicians see ward flags refresh
 * at their next login (the portal reads them from the login blob).
 */
export function WardConfigurationForm({ ward }: IWardConfigurationFormProps) {
  const { showToast } = useToast();
  const { updateWardConfiguration, isUpdatingWardConfiguration } =
    useUpdateWardConfiguration(Number(ward.id));

  const [form, setForm] = useState<IWardConfigurationFormState>(() =>
    toFormState(ward)
  );

  useEffect(() => {
    setForm(toFormState(ward));
  }, [ward]);

  const errors = {
    oneMstsPulseOxThreshold: thresholdError(form.oneMstsPulseOxThreshold),
    eMedMrnKey:
      form.flags.enableEMedIntegration && !form.eMedMrnKey.trim()
        ? "The eMed MRN key is required while the eMed integration is on."
        : undefined,
    spirometryResultTypes:
      form.spirometryResultTypes.length > 0
        ? undefined
        : "Select at least one spirometry result type.",
    enabledSmartDevices: smartDevicesError(form.enabledSmartDevices),
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const setFlag = (key: TWardFeatureFlagKey, value: boolean) =>
    setForm((previous) => ({
      ...previous,
      flags: applyWardFlagChange(previous.flags, key, value),
    }));

  const handleSave = async () => {
    if (hasErrors) return;

    const request: IUpdateWardConfigurationRequest = {
      onboardingStrategy: form.onboardingStrategy,
      ...form.flags,
      eMedMrnKey: form.eMedMrnKey.trim() || null,
      oneMstsPulseOxThreshold: form.oneMstsPulseOxThreshold.trim()
        ? Number(form.oneMstsPulseOxThreshold)
        : null,
      spirometryResultTypes: form.spirometryResultTypes,
      enabledSmartDevices:
        form.enabledSmartDevices.length > 0 ? form.enabledSmartDevices : null,
    };

    try {
      await updateWardConfiguration(request);
      showToast("The ward configuration was updated successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating this ward configuration."
        ),
        "warning"
      );
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitleGroup>
          <SectionTitle>Configuration</SectionTitle>
          <PageInfoTooltip title="Configuration">
            Feature flags and app settings for this ward&apos;s patients.
            Changes reach the patient app immediately.
          </PageInfoTooltip>
        </SectionTitleGroup>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel htmlFor="ward-onboarding-strategy">
            Onboarding strategy
          </FilterLabel>
          <AdminSelectField
            id="ward-onboarding-strategy"
            value={form.onboardingStrategy}
            onChange={(e) =>
              setForm((previous) => ({
                ...previous,
                onboardingStrategy: e.target.value as TOnboardingStrategy,
              }))
            }
          >
            {ONBOARDING_STRATEGY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            Which app flow newly enrolled patients are onboarded into.
          </FieldHint>
        </FilterGroup>

        <FilterGroup span="full">
          <FilterLabel htmlFor="ward-one-msts-threshold">
            1MSTS pulse-ox threshold (%)
          </FilterLabel>
          <FilterInput
            id="ward-one-msts-threshold"
            type="number"
            min={ONE_MSTS_THRESHOLD_MIN}
            max={ONE_MSTS_THRESHOLD_MAX}
            placeholder="App default"
            value={form.oneMstsPulseOxThreshold}
            onChange={(e) =>
              setForm((previous) => ({
                ...previous,
                oneMstsPulseOxThreshold: e.target.value,
              }))
            }
          />
          {errors.oneMstsPulseOxThreshold ? (
            <FieldError>{errors.oneMstsPulseOxThreshold}</FieldError>
          ) : (
            <FieldHint>
              SpO2 below which the app stops a 1-minute sit-to-stand test early.
              Leave empty for the app&apos;s built-in default. Patients in
              several wards get the lowest of their wards&apos; values.
            </FieldHint>
          )}
        </FilterGroup>

        <FilterGroup span="full">
          <FilterLabel htmlFor="ward-smart-devices">
            Enabled smart devices
          </FilterLabel>
          <SelectDropdown
            id="ward-smart-devices"
            multiple
            showSearch
            optionFilterProp="label"
            height={40}
            style={{ width: "100%" }}
            placeholder="Default device set (derived from the patient's condition)"
            options={SMART_DEVICE_OPTIONS.map((option) => ({
              key: option.value,
              value: option.value,
              label: option.label,
            }))}
            value={form.enabledSmartDevices}
            onValueChange={(selected) =>
              setForm((previous) => ({
                ...previous,
                enabledSmartDevices: Array.isArray(selected)
                  ? selected
                  : [selected],
              }))
            }
          />
          {errors.enabledSmartDevices ? (
            <FieldError>{errors.enabledSmartDevices}</FieldError>
          ) : (
            <FieldHint>
              Devices patients can pair in the app. Leave empty to offer the
              default set derived from each patient&apos;s condition type.
            </FieldHint>
          )}
        </FilterGroup>

        <FilterGroup span="full">
          <FilterLabel htmlFor="ward-spirometry-types">
            Spirometry result types
          </FilterLabel>
          <SelectDropdown
            id="ward-spirometry-types"
            multiple
            showSearch
            optionFilterProp="label"
            height={40}
            style={{ width: "100%" }}
            placeholder="Select result types"
            options={SPIROMETRY_RESULT_TYPE_OPTIONS.map((option) => ({
              key: option.value,
              value: option.value,
              label: option.label,
            }))}
            value={form.spirometryResultTypes}
            onValueChange={(selected) =>
              setForm((previous) => ({
                ...previous,
                spirometryResultTypes: Array.isArray(selected)
                  ? selected
                  : [selected],
              }))
            }
          />
          {errors.spirometryResultTypes ? (
            <FieldError>{errors.spirometryResultTypes}</FieldError>
          ) : (
            <FieldHint>
              Which values the app shows for a spirometry test result.
            </FieldHint>
          )}
        </FilterGroup>

        {WARD_FEATURE_SWITCH_GROUPS.map((group) => (
          <Fragment key={group.title}>
            <FormSubsection>
              <FormSubsectionTitle>{group.title}</FormSubsectionTitle>
            </FormSubsection>

            {group.switches.map((flag) => (
              <Fragment key={flag.key}>
                <FilterGroup>
                  <FlagRow>
                    <CheckboxField>
                      <input
                        type="checkbox"
                        checked={form.flags[flag.key]}
                        onChange={(e) => setFlag(flag.key, e.target.checked)}
                      />
                      {flag.label}
                    </CheckboxField>
                    <PageInfoTooltip title={flag.label}>
                      {flag.description}
                    </PageInfoTooltip>
                  </FlagRow>
                </FilterGroup>

                {/* The MRN key sits right after the eMed switch so the pair shares a row. */}
                {flag.key === "enableEMedIntegration" && (
                  <FilterGroup key="ward-emed-mrn-key">
                    <FilterLabel htmlFor="ward-emed-mrn-key">
                      eMed MRN key
                    </FilterLabel>
                    <AdminSelectField
                      id="ward-emed-mrn-key"
                      disabled={!form.flags.enableEMedIntegration}
                      value={form.eMedMrnKey}
                      onChange={(e) =>
                        setForm((previous) => ({
                          ...previous,
                          eMedMrnKey: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select an option</option>
                      {EMED_MRN_KEY_OPTIONS.map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </AdminSelectField>
                    {errors.eMedMrnKey && (
                      <FieldError>{errors.eMedMrnKey}</FieldError>
                    )}
                  </FilterGroup>
                )}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </FormGrid>

      <SectionActions>
        <FilterButton
          variant="primary"
          disabled={isUpdatingWardConfiguration || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingWardConfiguration ? "Saving..." : "Save configuration"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
